<template>
  <div class="multiline-select">
    <div class="multiline-select__row">
      <multiline-select-item
        v-bind:item-option-list="itemOptionList"
        v-bind:disabled="disabled"
        v-bind:initial-value="headValue"
        v-bind:placeholder="label"
        v-bind:hint="hint"
        v-on:change="onChangeHead" />
      <button
        class="multiline-select__button-add"
        v-bind:disabled="!isAllowedMore"
        v-if="isShowAddItemButton"
        type="button"
        v-on:click="addItem">+ Добавить</button>
    </div>
    <div
      class="multiline-select__row"
      v-bind:key="key"
      v-for="{ value, key } in additionalItemList">
      <multiline-select-item
        class="multiline-select__additional-field"
        v-bind:disabled="disabled"
        v-bind:initial-value="value"
        v-bind:item-option-list="itemOptionList"
        v-bind:placeholder="label"
        v-on:change="onChangeItem(key, $event)" />
      <button
        class="multiline-select__button-remove"
        type="button"
        v-if="!disabled"
        v-on:click="removeItem(key)">- Удалить</button>
    </div>
    <div
      v-if="hasViolations"
    ><p
      class="text__violation"
      role="alert"
      v-for="violationText in violations"
      v-bind:key="violationText"
    >{{ violationText }}</p>
    </div>
  </div>
</template>
<script>
import MultilineSelectItem from '@/components/MultilineSelectItem.vue';

/**
 * @description Пустое значение элемента выбора.
 * @type {string}
 */
const EMPTY_ITEM_VALUE = '';

/**
 * @description Название события изменения в значениях.
 * @type {string}
 */
const NAME_EVENT_WATCH_VALUES = 'input';

/**
 * @description Опция выбора.
 * @param {boolean} isDisabled Отключен.
 * @param {string}  title      Заголовок.
 * @param {string}  value      Значение.
 * @constructor
 */
function Option(isDisabled, title, value) {
  this.isDisabled = isDisabled;
  this.title = title;
  this.value = value;
}

/**
 * @description Компонент многострочного выбора.
 * @type {Vue}
 */
export default {
  components: {
    MultilineSelectItem,
  },
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0;
    },
    /**
     * @description Список классов корневого элемента.
     * @type {string[]}
     */
    rootClasses() {
      const list = [];
      if (this.hasViolations) {
        list.push('invalid');
      }
      return list;
    },
    /**
     * @description Доступно добавление полей.
     * @type {boolean}
     */
    isAllowedMore() {
      const maxNumberAdditionalItems = Math.min(
        this.itemOptionList.length - 1,
        this.maxSelectCount === -1 ? this.itemOptionList.length - 1 : this.maxSelectCount - 1,
      );
      return this.additionalItemList.length < maxNumberAdditionalItems;
    },
    /**
     * @description Показывать кнопку добавления элемента (рядя).
     * @type {boolean}
     */
    isShowAddItemButton() {
      return this.isAllowedMore && !this.disabled;
    },
    /**
     * @description Список элементов опций.
     * @type {Option[]}
     */
    itemOptionList: (vm) => vm.items.map(({ value, title }) => new Option(
      vm.values.includes(value),
      title,
      value,
    )),
    /**
     * @description Выбранные значения.
     * @type {any[]}
     */
    values() {
      const isFull = (value) => EMPTY_ITEM_VALUE !== value;
      const isUnique = (value, index, haystack) => haystack.indexOf(value) === index;
      return [this.headValue, ...this.additionalItemList.map((item) => item.value)]
        .filter(isFull)
        .filter(isUnique);
    },
  },
  data() {
    return {
      /**
       * @description Дополнительные элементы (ряды).
       * @type {{key: number, value: any}}
       */
      additionalItemList: (this.value.slice(1, this.value.length) || []).map((item) => ({
        key: item,
        value: item,
      })),
      /**
       * @description Значение головного элемента (ряда).
       * @type {any}
       */
      headValue: this.value[0] || EMPTY_ITEM_VALUE,
    };
  },
  methods: {
    /**
     * @description Добавить элемент (ряд).
     * @returns {void}
     */
    addItem() {
      const item = {
        key: Date.now(),
        value: EMPTY_ITEM_VALUE,
      };
      this.additionalItemList.push(item);
    },
    /**
     * @description Обработать изменение головного элемента (ряда).
     * @returns {void}
     */
    onChangeHead(value) {
      this.headValue = value;
    },
    /**
     * @description Обработать изменение дополнительного элемента (ряда).
     * @returns {void}
     */
    onChangeItem(key, value) {
      this.additionalItemList = this
        .additionalItemList
        .map((item) => (key === item.key ? { ...item, value } : item));
    },
    /**
     * @description Удалить элемент (ряд).
     * @returns {void}
     */
    removeItem(key) {
      this.additionalItemList = this.additionalItemList.filter((item) => key !== item.key);
    },
  },
  name: 'MultilineSelect',
  props: {
    /**
     * @description Компонент деактивирован.
     * @type {number}
     */
    disabled: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      type: Array,
    },
    /**
     * @description Данные опций элементов (рядов).
     * @type {{title: string, value: string}[]}
     */
    items: {
      required: true,
      type: Array,
    },
    /**
     * @description Заголовок элемента (ряда).
     * @type {string}
     */
    label: {
      required: true,
      type: String,
    },
    hint: {
      required: false,
      type: String,
      default: '',
    },
    /**
     * @description Изначальное значение.
     * @type {Array}
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * @description Максимальное количество выбранных элементов.
     * @type {number}
     */
    maxSelectCount: {
      type: Number,
      default: () => -1,
    },
  },
  watch: {
    values(selectedValues) {
      this.$emit(NAME_EVENT_WATCH_VALUES, selectedValues);
    },
    value(value) {
      this.headValue = value[0] || EMPTY_ITEM_VALUE;
      if (value !== this.values) {
        this.additionalItemList = (value.slice(1, value.length) || []).map((item) => ({
          key: item,
          value: item,
        }));
      }
    },
  },
};
</script>
<style scoped lang="scss">
.multiline-select {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 25px;
  &.invalid .text__input{
    color: var(--warning-color);
  }
  &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
  }
  &__row {
    display: grid;
    grid-template-columns: 2fr 0.98fr;
    grid-gap: 10px;
    align-items: center;
  }
  &__button {
    &-add {
      width: fit-content;
      text-align: left;
      color: #0489e0;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
    }
    &-remove {
      text-align: left;
      color: var(--warning-color);
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
    }
  }
}
@media screen and (max-width: 520px) {
  .multiline-select {
    &__row {
      grid-template-columns: 1fr;
    }
  }
}
</style>
